import { readSetting, writeSetting } from '../../infrastructure/storage/localStore';

/**
 * What the jukebox remembers about this device (SPEC-014 PR-14.2).
 *
 * Two settings, and the shape of each one is a decision rather than a default.
 *
 * THERE IS NO VOLUME HERE EITHER. It was a stored level and a slider until iOS
 * turned out to ignore `element.volume` outright; the control went, and with it
 * the only thing that ever wrote this. Loudness is the device's (SPEC-014 D-18).
 *
 * THERE IS NO "ON". The duel's ticks store an OPT-OUT — sound is on unless the
 * reader turned it off — because a clock that only ticks once you have found its
 * switch is a feature most readers never meet (`duelSounds.js`). Music is the
 * opposite case and the reasoning inverts cleanly: a page that starts playing a
 * three-minute track the moment it opens is the single most complained-about
 * behaviour on the web, every mobile browser blocks it outright, and the reader
 * who wanted music yesterday may be on a train today. So playing is never
 * restored, only the shelf position is, and the jukebox opens paused every time.
 *
 * Nothing here is sensitive and nothing here is load-bearing: every read has a
 * defaulted answer, because `localStore` returns `null` for a device with
 * storage disabled and Safari in Private mode throws on the way past.
 */

const SHUFFLE_KEY = 'jukebox:shuffle';
const TRACK_KEY = 'jukebox:track';

/**
 * Whether the shelf is shuffled. IN ORDER unless the reader asked for a shuffle,
 * and the stored value is the OPT-IN — the opposite shape to the duel's ticks,
 * which store an opt-out because a clock nobody can find never ticks.
 *
 * IT HAS BEEN BOTH WAYS, and each time the answer followed the SHELF rather than
 * a preference about jukeboxes. It defaulted off first, on the argument that the
 * build's order is a running order somebody chose and a first-time listener
 * should hear it before a shuffle decides for them. The shelf then went to twenty
 * rows with each title's takes sitting together, which made that order hand a
 * listener the same song three times in a row — an argument for shuffling that
 * was really an admission the order was a catalogue — and the default inverted.
 *
 * THE SHELF IS A SEQUENCE NOW (`soundtrackShelf.js`, SPEC-014 D-27): styles
 * alternate, tempos step by fifteen BPM at the closest, a title's takes are four
 * rows apart, and the whole thing is shaped from an opener to a close. So the
 * original argument holds again, and the default is back where it started —
 * played top to bottom this shelf is worth hearing top to bottom, and in-order
 * play is handed the order literally rather than spread (`spreadFamilies` is not
 * applied to it).
 *
 * A DEVICE THAT ASKED FOR SHUFFLE KEEPS IT. The stored `'on'` is only ever
 * written by the button, so a reader who chose it is unaffected by the flip;
 * what changes is what a device that has never touched the switch is given.
 */
export function readJukeboxShuffle() {
    return readSetting(SHUFFLE_KEY) === 'on';
}

export function writeJukeboxShuffle(isShuffled) {
    return writeSetting(SHUFFLE_KEY, isShuffled ? 'on' : 'off');
}

/**
 * The track this device was last on, by id.
 *
 * By ID rather than by index, because the shelf is data written by a build: a
 * stored `4` means a different song the day a row is added to `TRACKS`, and it
 * means nothing at all the day the shelf gets shorter. An id that is no longer
 * on the shelf simply does not match, and the jukebox opens on the first track.
 *
 * The POSITION within the track is deliberately not stored. Resuming a song
 * ninety seconds in is what a podcast wants; a jukebox that did it would be
 * starting every session in the middle of a chorus.
 */
export function readJukeboxTrackId() {
    return readSetting(TRACK_KEY);
}

export function writeJukeboxTrackId(trackId) {
    return writeSetting(TRACK_KEY, trackId);
}
